/**
 * Provides helper functions for creating and manipuating esri/layers/FeatureLayer
 */
window.FeatureLayerHelper = {
    featureLayerSelectedGraphics: [],

    /**
     * Create a FeatureLayer
     */
    createFeatureLayer(layerId, options = {}) {
        if (!layerId) {
            return;
        }

        return new this.modules.FeatureLayer({
            id: layerId,
            objectIdField: options.objectIdField || 'OID',
            fields: options.fields || [],
            geometryType: options.geometryType || 'point',
            source: options.graphics || [],
            renderer: options.renderer || {},
            popupEnabled: options.popupEnabled || true,
            popupTemplate: options.popupTemplate || {},
            labelingInfo: options.labelingInfo || [],
            labelsVisible: !!options.labelsVisible,
            visible: typeof options.isLayerVisible === 'boolean' ? options.isLayerVisible : true,
            spatialReference: options.spatialReference || { wkid: 4326 }
        });
    },

    /**
     * Add a FeatrueLayer to the map. Default layerIndex = 0 and the layer will be added above all layers
     */
    addFeatureLayer(layerId, options, layerIndex = 0) {
        const layer = this.createFeatureLayer(layerId, options);
        if (!layer) {
            return;
        }
        layer.outFields = ['*'];

        this.map.add(layer, layerIndex);
        return true;
    },

    /*
     * Allow update the definition of a featureLayer. For now we only support updating renderers
     */
    updateFeatureLayer(layerId, options = {}) {
        const layer = this.map.findLayerById(layerId);
        if (!layer) {
            return;
        }

        if (options.renderer) {
            layer.renderer = options.renderer;
        }

        return true;
    },

    /**
     * Set the visibility of the layer
     */
    toggleFeatureLayerVisibility(layerId, visible) {
        const layer = this.map.findLayerById(layerId);
        layer.visible = visible;
    },

    /**
     * Set the visibility of the layer's label
     */
    toggleFeatureLayerLabelVisibility(layerId, visible) {
        const layer = this.map.findLayerById(layerId);
        layer.labelsVisible = visible;
    },

    /**
     * Show/Filter the FeatureLayer graphics using the given whereClause (a SQL query statement)
     */
    featureLayerUpdateGraphicsVisibility(layerId, whereClause) {
        const layer = this.map.findLayerById(layerId);
        if (!layer || !whereClause) {
            return;
        }
        layer.definitionExpression = whereClause;
    },

    /**
     * Replace the layer's all existing graphics using the incoming array of esri/Graphic
     */
    async featureLayerReplaceGraphics(layerId, idFieldName, toUpdateGraphics) {
        const layer = this.map.findLayerById(layerId);
        if (!layer) {
            return;
        }

        // We must call queryFeatures before applying edits since the featureLayer was created using client-side (non-server based) graphics
        // https://community.esri.com/thread/223433-v49-feature-layer-applyedits-bug#comments
        const currentGraphics = await this.featureLayerGetGraphics(layerId);
        const {
            addFeatures,
            updateFeatures,
            deleteFeatures
        } = this._featureLayerGroupGraphicsByEditStates(
            idFieldName,
            currentGraphics,
            toUpdateGraphics
        );
        if (addFeatures.length > 0 || updateFeatures.length > 0 || deleteFeatures.length > 0) {
            const result = await layer.applyEdits({ addFeatures, updateFeatures, deleteFeatures });
            this._printEditErrors(result);
            return result;
        }
    },

    _featureLayerGroupGraphicsByEditStates(
        idFieldName,
        currentGraphics = [],
        toUpdateGraphics = []
    ) {
        if (!idFieldName) {
            return {};
        }

        const currentGraphicsTable = this._createIdGraphicHashTable(idFieldName, currentGraphics);
        const toUpdateGraphicsTable = this._createIdGraphicHashTable(idFieldName, toUpdateGraphics);

        const addFeatures = [],
            updateFeatures = [],
            deleteFeatures = [];
        const allIds = new Set([
            ...Object.keys(currentGraphicsTable),
            ...Object.keys(toUpdateGraphicsTable)
        ]);
        allIds.forEach(id => {
            const currentGraphic = currentGraphicsTable[id];
            const toUpdateGraphic = toUpdateGraphicsTable[id];

            if (currentGraphic && toUpdateGraphic) {
                this.updateGraphicProperties(currentGraphic, {
                    geometry: toUpdateGraphic.geometry,
                    attributes: toUpdateGraphic.attributes
                });
                updateFeatures.push(currentGraphic);
            } else if (toUpdateGraphic) {
                addFeatures.push(toUpdateGraphic);
            } else if (currentGraphic) {
                deleteFeatures.push(currentGraphic);
            }
        });

        return { addFeatures, updateFeatures, deleteFeatures };
    },

    _createIdGraphicHashTable(idFieldName, graphics) {
        return graphics.reduce((table, graphic) => {
            const id = graphic.attributes[idFieldName];
            if (id) {
                table[id] = graphic;
            }
            return table;
        }, {});
    },

    /**
     *  Add graphics to a FeatureLayer
     */
    async featureLayerAddGraphics(layerId, graphics) {
        if (!layerId || !graphics) {
            return;
        }

        const layer = this.map.findLayerById(layerId);
        if (!layer) {
            return;
        }

        const toAddGraphics = Array.isArray(graphics) ? graphics.filter(g => g) : [graphics];
        const result = await layer.applyEdits({ addFeatures: toAddGraphics });
        this._printEditErrors(result);
        return result;
    },

    /**
     *  Update the given graphics of the FeatureLayer. toUpdateFeatures is an array of esri/Graphics
     */
    async featureLayerUpdateGraphics(layerId, idFieldName, toUpdateGraphics) {
        const layer = this.map.findLayerById(layerId);
        if (!layer || toUpdateGraphics.length === 0) {
            return;
        }

        const currentGraphics = await this.featureLayerGetGraphics(layerId);
        const updatedGraphics = [];
        toUpdateGraphics.forEach(toUpdateGraphic => {
            const currentGraphic = currentGraphics.find(
                g => g.attributes[idFieldName] === toUpdateGraphic.attributes[idFieldName]
            );
            if (currentGraphic) {
                this.updateGraphicProperties(currentGraphic, {
                    geometry: toUpdateGraphic.geometry,
                    attributes: toUpdateGraphic.attributes
                });
                updatedGraphics.push(currentGraphic);
            }
        });

        if (updatedGraphics.length > 0) {
            const result = await layer.applyEdits({ updateFeatures: updatedGraphics });
            this._printEditErrors(result);
            return result;
        }
    },

    /**
     * Remove the layer's graphics with the Ids matching the given Ids
     */
    async featureLayerRemoveGraphics(layerId, idFieldName, toUpdateFeatureIds) {
        const layer = this.map.findLayerById(layerId);
        if (!layer || !toUpdateFeatureIds) {
            return;
        }

        const currentGraphics = await this.featureLayerGetGraphics(layerId);
        const deleteFeatures = currentGraphics.filter(graphic =>
            toUpdateFeatureIds.includes(graphic.attributes[idFieldName])
        );
        if (deleteFeatures.length === 0) {
            return;
        }
        const result = await layer.applyEdits({ deleteFeatures });
        this._printEditErrors(result);
        return result;
    },

    /**
     * Get the FeatureLayer graphics using the given whereClause (a SQL query statement)
     */
    async featureLayerGetGraphics(layerId, whereClause) {
        const layer = this.map.findLayerById(layerId);
        if (!layer) {
            return;
        }

        const where = whereClause && typeof whereClause === 'string' ? whereClause : '1 = 1';
        const query = {
            where,
            outFields: ['*'],
            returnGeometry: true
        };

        return await layer.queryFeatures(query).then(response => response.features);
    },

    /**
     * Get the FeatureLayer graphics using the input geometry and specified spatial relationship
     * Currently this method only supports polygon as the input geometry
     * "spatial relationship" is one of the strings listed here: https://developers.arcgis.com/javascript/latest/api-reference/esri-tasks-support-Query.html#spatialRelationship
     */
    async featureLayerSpatialQuery(layerId, geometry, spatialRelationship = 'intersects') {
        const layer = this.map.findLayerById(layerId);
        if (!layer || !geometry || geometry.rings.length === 0) {
            return [];
        }

        const query = {
            geometry,
            spatialRelationship,
            outFields: ['*'],
            returnGeometry: false
        };

        // Since we're running a client side query against a featureLayer, we need to use featureLayerView to run the query
        // See https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-FeatureLayer.html#queryFeatures for details
        const layerView = await this.view.whenLayerView(layer);
        const featureSet = await layerView.queryFeatures(query);
        return featureSet.features;
    },

    _printEditErrors({ addFeatureResults, updateFeatureResults, deleteFeatureResults }) {
        [...addFeatureResults, ...updateFeatureResults, ...deleteFeatureResults].forEach(result => {
            result.error && console.log(result.error.message);
        });
    }
};
